//core
import { createSelector } from "@reduxjs/toolkit";

//store
import { RootState } from "../../index";
import { outlinedInputClasses } from "@mui/material";
import { MuiButtonsOverrides } from "../../../components/UI/MuiTheme/general/buttonsOverrides";
import { themes as localThemes } from "../../../components/UI/MuiTheme";

//uploadConfig
const uploadConfigDataSelector = (state: RootState) => state.configuration.upload_config.data;
export const selectUploadConfigData = createSelector(uploadConfigDataSelector, (data) => data);

// Branding
const brandingDataSelector = (state: RootState) => state.configuration.branding.data;
const brandingErrorSelector = (state: RootState) => state.configuration.branding.error;
const brandingFetchingSelector = (state: RootState) => state.configuration.branding.isFetching;

//selectors
export const selectBrandingData = createSelector(brandingDataSelector, (data) => {
	if (!!data) {
		let sourceThemes = data.themes;

		if (!sourceThemes || sourceThemes.length === 0) {
			const brand = process.env.REACT_APP_BRAND || 'pma';
			const defaultTheme = process.env.REACT_APP_THEME || '';
			const defaultMode = defaultTheme.includes('_dark') ? 'dark' : 'light';
			const lightKey = `${brand}_light`;
			const darkKey = `${brand}_dark`;
			sourceThemes = [
				...(localThemes[lightKey] ? [{ mode: 'light', isDefault: defaultMode === 'light', palette: localThemes[lightKey] }] : []),
				...(localThemes[darkKey] ? [{ mode: 'dark', isDefault: defaultMode === 'dark', palette: localThemes[darkKey] }] : []),
			];
		}

		const themes = sourceThemes.map((item: any) => {
			if (item.mode === "light") {
				return {
					...item,
					palette: {
						spacing: 8,
						...item.palette,
						palette: {
							mode: 'light',
							...item.palette.palette,
						},
						components: {
							...item.palette.components,
							MuiButton: MuiButtonsOverrides,
							...(process.env.REACT_APP_BRAND === 'bat' ? {
								MuiTextField: {
									styleOverrides: {
										root: {
											"& .MuiOutlinedInput-root": {
												"& > fieldset": {
													borderColor: 'rgba(0,0,0,0.54)',
												},
											},
										},
									},
								},
								MuiOutlinedInput: {
									styleOverrides: {
										root: {
											[`& .${outlinedInputClasses.notchedOutline}`]: {
												borderColor: 'rgba(0,0,0,0.54)',
											}
										}
									}
								},
								MuiSelect: {
									styleOverrides: {
										select: {
											"& .MuiSelect-select": {
												"& > fieldset": {
													borderColor: 'rgba(0,0,0,0.54)',
												},
											},
										},
									}
								},
								MuiSwitch: {
									styleOverrides: {
										track: {
											backgroundColor: 'rgba(0,0,0,0.54)',
										}
									}
								},
							} : {}),
						},
					},
				};
			} else {
				return {
					...item,
					palette: {
						spacing: 8,
						...item.palette,
						palette: {
							mode: 'dark',
							...item.palette.palette,
						},
						components: {
							...item.palette.components,
							MuiButton: MuiButtonsOverrides,
							MuiTextField: {
								styleOverrides: {
									root: {
										"& .MuiOutlinedInput-root": {
											"& > fieldset": {
												borderColor: item.palette.palette.other.outlinedBorder, //other.outlinedBorder
											},
											"&.Mui-focused": {
												"& > fieldset": {
													borderColor: item.palette.palette.primary.main, //primary.main
												}
											},
											"&:hover": {
												"& > fieldset": {
													borderColor: item.palette.palette.primary.main, //primary.main
												}
											},
										},
										"& > label": {
											"&.Mui-focused": {
												color: item.palette.palette.primary.main, //primary.main
											},
										}
									},
								},
							},
							MuiOutlinedInput: {
								styleOverrides: {
									root: {
										"&.Mui-focused": {
											"& > fieldset": {
												borderColor: `${item.palette.palette.primary.main} !important`, //primary.main
											},
										},

										[`& .${outlinedInputClasses.notchedOutline}`]: {
											borderColor: item.palette.palette.other.outlinedBorder, //other.outlinedBorder
											"& > fieldset": {
												borderColor: item.palette.palette.other.outlinedBorder, //other.outlinedBorder
											},
											"&.Mui-focused": {
												"& > fieldset": {
													borderColor: item.palette.palette.primary.main, //primary.main
												}
											},
											"&:hover": {
												"& > fieldset": {
													borderColor: item.palette.palette.primary.main, //primary.main
												}
											},
										},
										"& > label": {
											"&.Mui-focused": {
												color: item.palette.palette.primary.main, //primary.main
											},
										}
									}
								}
							},
							MuiSelect: {
								styleOverrides: {
									select: {
										"& .MuiSelect-select": {
											"& > fieldset": {
												borderColor: item.palette.palette.other.outlinedBorder, //other.outlinedBorder
											},
											"&.Mui-focused": {
												"& > fieldset": {
													borderColor: item.palette.palette.primary.main, //primary.main
												}
											},
											"&:hover": {
												"& > fieldset": {
													borderColor: item.palette.palette.primary.main, //primary.main
												}
											},
										},
										"& > label": {
											"&.Mui-focused": {
												color: item.palette.palette.primary.main, //primary.main
											},
										}
									},
								}
							},
							MuiInputLabel: {
								styleOverrides: {
									root: {
										"&.Mui-focused": {
											color: item.palette.palette.primary.main //primary.main
										},
									}
								}
							},
							MuiTooltip: {
								styleOverrides: {
									tooltip: {
										background: 'rgba(97,97,97,0.9)',
									}
								}
							},
							MuiSwitch: {
								styleOverrides: {
									track: {
										//borderColor: 'rgba(255, 255, 255, 0.23)', //other.outlinedBorder
									}
								}
							},
						},
					},
				};
			}
		});

		return { ...data, themes: themes };
	} else {
		return data;
	}
});
export const selectBrandingError = createSelector(brandingErrorSelector, (error) => error);
export const selectBrandingIsFetching = createSelector(brandingFetchingSelector, (isFetching) => isFetching);

// derived branding selectors

const brandingImagesSelector = (state: RootState) => state.configuration.branding?.data?.images;
const brandingLinksSelector = (state: RootState) => state.configuration.branding?.data?.links;
const brandingSupportEmailSelector = (state: RootState) => state.configuration.branding?.data?.supportEmail;
const brandingPhoneMaskCountrySelector = (state: RootState) => state.configuration.branding?.data?.phoneMaskCountry;
const brandingThemesSelector = (state: RootState) => state.configuration.branding?.data?.themes;

export const selectBrandingImages = createSelector(brandingImagesSelector, (images) => images);
export const selectBrandingLinks = createSelector(brandingLinksSelector, (links) => links);
export const selectBrandingSupportEmail = createSelector(brandingSupportEmailSelector, (email) => email);
export const selectBrandingPhoneMaskCountry = createSelector(brandingPhoneMaskCountrySelector, (country) => country);
export const selectBrandingThemes = createSelector(brandingThemesSelector, (themes) => themes);